/**
 * InboundExportSection — portability export (DSGVO Art. 20 / [I-7], WL-A8, §7.6).
 *
 * Owner-only self-service "export all my received data" panel on the InboxPage:
 *  - a button that calls `requestPortabilityExport` (starts a strictly
 *    tenant-scoped ZIP job on the server),
 *  - a live status line driven by an `onSnapshot` listener on the caller's most
 *    recent `inbound_export_jobs` doc (queued → running → done / error),
 *  - a download link once the ZIP is ready (7-day signed URL from the job).
 *
 * Anti-lock-in framing only ("your data is yours") — never a premium gate. Wording
 * follows the §10 whitelist (no "geprüft/rechtssicher/GoBD" claims).
 *
 * Flag-gated: only rendered when `INBOUND_ENABLED_FLAG` is on (the whole InboxPage
 * route is flag-gated too, this is a defensive second gate).
 *
 * @module InboundExportSection
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconFileArchive, IconDownload, IconSpinner } from '../../icons';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { requestPortabilityExport, normalizeCallableError } from '../../api/inboundApi';
import logger from '../../utils/logger';

/** Lifecycle status of an `inbound_export_jobs` doc (§5.8). */
type ExportJobStatus = 'queued' | 'running' | 'done' | 'error';

/** The slice of `inbound_export_jobs/{id}` this panel reads. */
interface ExportJobView {
  status: ExportJobStatus;
  downloadUrl: string | null;
  urlExpiresAt: Timestamp | null;
  docCount: number | null;
  createdAt: Timestamp | null;
}

interface InboundExportSectionProps {
  /** The owner's UID (== `inbound_export_jobs.userId`) — the isolation anchor. */
  ownerUid: string;
}

/** Whether a signed download URL is still within its validity window. */
function urlStillValid(job: ExportJobView): boolean {
  if (!job.downloadUrl) return false;
  if (!job.urlExpiresAt) return true; // no expiry recorded → treat as usable
  try {
    return job.urlExpiresAt.toMillis() > Date.now();
  } catch {
    return true;
  }
}

const InboundExportSection: React.FC<InboundExportSectionProps> = ({ ownerUid }) => {
  const { t } = useTranslation('posteingang');
  const [job, setJob] = useState<ExportJobView | null>(null);
  const [starting, setStarting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // ── Live listener on the caller's most recent export job ────────────────────
  useEffect(() => {
    if (!ownerUid) return undefined;
    const q = query(
      collection(db, 'inbound_export_jobs'),
      where('userId', '==', ownerUid),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) {
          setJob(null);
          return;
        }
        const data = snap.docs[0].data() as Partial<ExportJobView> & {
          status?: string;
        };
        setJob({
          status: (data.status as ExportJobStatus) ?? 'queued',
          downloadUrl: typeof data.downloadUrl === 'string' ? data.downloadUrl : null,
          urlExpiresAt: data.urlExpiresAt instanceof Timestamp ? data.urlExpiresAt : null,
          docCount: typeof data.docCount === 'number' ? data.docCount : null,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt : null,
        });
      },
      (err) => {
        // Non-critical: the button still works without live status.
        logger.error('InboundExportSection: job listener failed', err);
      },
    );
    return () => unsub();
  }, [ownerUid]);

  const inProgress = job?.status === 'queued' || job?.status === 'running';

  const handleExport = useCallback(async () => {
    if (starting || inProgress) return;
    setStarting(true);
    setErrorKey(null);
    try {
      await requestPortabilityExport();
      // The snapshot listener picks up the new `queued` job automatically.
    } catch (err) {
      const norm = normalizeCallableError(err);
      // Map the two expected business codes to friendly copy; else generic.
      if (norm.code === 'resource-exhausted') {
        setErrorKey('export.errorRateLimited');
      } else if (norm.code === 'failed-precondition') {
        setErrorKey('export.errorNotReady');
      } else {
        setErrorKey('export.errorGeneric');
      }
    } finally {
      setStarting(false);
    }
  }, [starting, inProgress]);

  const readyForDownload = job?.status === 'done' && urlStillValid(job);

  return (
    <div className="bg-surface-raised border border-hairline rounded-xl p-4 md:p-5">
      <div className="flex items-center gap-2 mb-2">
        <IconFileArchive className="text-accent-ink" />
        <h2 className="text-primary font-semibold">
          {t('export.title', 'Ihre Empfangsdaten exportieren')}
        </h2>
      </div>

      <p className="text-sm text-muted mb-4">
        {t(
          'export.body',
          'Ihre Daten gehören Ihnen. Laden Sie alle empfangenen Belege — Originaldateien, E-Mails und ein Inhaltsverzeichnis (index.csv) — jederzeit als ZIP-Archiv herunter. Kein Premium nötig.',
        )}
      </p>

      {/* Secondary CTA (polish pass posteingang-polish, Auflage #6): the
          copy-template action in InboundSetupGuide is the ONE primary CTA on
          this page — this export action uses a Mint outline/ghost style
          (transparent background) so it does not compete for attention. */}
      <button
        onClick={handleExport}
        disabled={starting || inProgress}
        className="inline-flex items-center gap-2 text-sm bg-transparent border border-indigo-500/60 text-accent-ink hover:bg-indigo-500/10 disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2 rounded-lg font-medium transition-colors"
      >
        {starting || inProgress ? (
          <IconSpinner className="animate-spin" size={12} />
        ) : (
          <IconFileArchive size={12} />
        )}
        {t('export.button', 'Meine Empfangsdaten exportieren')}
      </button>

      {/* Live status */}
      {inProgress && (
        <p className="mt-3 text-sm text-accent-ink flex items-center gap-2">
          <IconSpinner className="animate-spin" size={12} />
          {t('export.status.running', 'Export wird erstellt — das kann einen Moment dauern …')}
        </p>
      )}

      {readyForDownload && job?.downloadUrl && (
        <div className="mt-3">
          <a
            href={job.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <IconDownload size={12} />
            {t('export.status.download', 'ZIP-Archiv herunterladen')}
          </a>
          <p className="mt-2 text-xs text-faint">
            {typeof job.docCount === 'number'
              ? t('export.status.readyCount', '{{count}} Belege · Link 7 Tage gültig.', {
                  count: job.docCount,
                })
              : t('export.status.ready', 'Link 7 Tage gültig.')}
          </p>
        </div>
      )}

      {job?.status === 'done' && !urlStillValid(job) && (
        <p className="mt-3 text-sm text-muted">
          {t('export.status.expired', 'Der letzte Download-Link ist abgelaufen. Starten Sie den Export erneut.')}
        </p>
      )}

      {job?.status === 'error' && (
        <p className="mt-3 text-sm text-[var(--tone-warning-fg)]">
          {t('export.status.error', 'Der Export ist fehlgeschlagen. Bitte versuchen Sie es erneut.')}
        </p>
      )}

      {errorKey && (
        <p className="mt-3 text-sm text-[var(--tone-warning-fg)]">
          {errorKey === 'export.errorRateLimited' &&
            t('export.errorRateLimited', 'Sie haben heute bereits einen Export gestartet. Bitte versuchen Sie es morgen erneut.')}
          {errorKey === 'export.errorNotReady' &&
            t('export.errorNotReady', 'Der Export steht für Ihr Konto noch nicht bereit.')}
          {errorKey === 'export.errorGeneric' &&
            t('export.errorGeneric', 'Der Export konnte nicht gestartet werden. Bitte versuchen Sie es erneut.')}
        </p>
      )}
    </div>
  );
};

export default InboundExportSection;
